// Local API Service for Metal Commodities
// Uses backend server with Firestore for historical comparison

const MONTHLY_CALL_LIMIT = 80;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export interface MetalPrice {
    symbol: string;
    name: string;
    price: number;
    priceAED: number;
    change: number;
    changePercent: number;
    unit: string;
    unitAED: string;
    lastUpdated: string;
}

export interface MetalPricesData {
    metals: MetalPrice[];
    timestamp: number;
    source: 'api' | 'cache';
}

interface RateLimitTracker {
    callCount: number;
    resetDate: string; // ISO date string for monthly reset
    lastCallTimestamp: number;
}

interface CachedData {
    data: MetalPricesData;
    cachedAt: number;
}

class MetalPriceService {
    private rateLimitKey = 'metalPriceRateLimit';
    private cacheKey = 'metalPriceCache';

    /**
     * Get rate limit tracker from localStorage
     */
    private getRateLimitTracker(): RateLimitTracker {
        const stored = localStorage.getItem(this.rateLimitKey);
        if (!stored) {
            return this.initializeRateLimitTracker();
        }

        const tracker: RateLimitTracker = JSON.parse(stored);

        // Check if we need to reset (new month)
        const resetDate = new Date(tracker.resetDate);
        const now = new Date();

        if (now > resetDate) {
            return this.initializeRateLimitTracker();
        }

        return tracker;
    }

    /**
     * Initialize a new rate limit tracker
     */
    private initializeRateLimitTracker(): RateLimitTracker {
        const now = new Date();
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        const tracker: RateLimitTracker = {
            callCount: 0,
            resetDate: nextMonth.toISOString(),
            lastCallTimestamp: 0
        };

        localStorage.setItem(this.rateLimitKey, JSON.stringify(tracker));
        return tracker;
    }

    /**
     * Increment API call count
     */
    private incrementCallCount(): void {
        const tracker = this.getRateLimitTracker();
        tracker.callCount += 1;
        tracker.lastCallTimestamp = Date.now();
        localStorage.setItem(this.rateLimitKey, JSON.stringify(tracker));
    }

    /**
     * Check if we can make an API call
     */
    private canMakeApiCall(): boolean {
        const tracker = this.getRateLimitTracker();
        return tracker.callCount < MONTHLY_CALL_LIMIT;
    }

    /**
     * Get cached data if valid
     */
    private getCachedData(): MetalPricesData | null {
        const stored = localStorage.getItem(this.cacheKey);
        if (!stored) return null;

        const cached: CachedData = JSON.parse(stored);
        const now = Date.now();

        // Check if cache is still valid (within 24 hours)
        if (now - cached.cachedAt < CACHE_DURATION) {
            return {
                ...cached.data,
                source: 'cache'
            };
        }

        // Return cached data but mark as stale
        return {
            ...cached.data,
            source: 'cache'
        };
    }

    /**
     * Save data to cache
     */
    private saveToCache(data: MetalPricesData): void {
        const cached: CachedData = {
            data,
            cachedAt: Date.now()
        };
        localStorage.setItem(this.cacheKey, JSON.stringify(cached));
    }

    /**
     * Fetch metal prices from our backend API
     */
    private async fetchFromApi(forceRefresh: boolean = false): Promise<MetalPricesData> {
        try {
            // Call our new backend API endpoint
            const url = `/api/metal-prices${forceRefresh ? '?refresh=true' : ''}`;
            console.log('Fetching metal prices from backend:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();

            const result: MetalPricesData = {
                ...data,
                source: 'api'
            };

            // Increment call count
            this.incrementCallCount();

            // Save to cache
            this.saveToCache(result);

            return result;
        } catch (error) {
            console.error('Error fetching from Backend API:', error);
            throw error;
        }
    }

    /**
     * Get metal prices - uses cache if available, only fetches from API when forced.
     * Returns null if API limit is exhausted and no valid cache exists — UI should hide rates.
     */
    public async getMetalPrices(forceRefresh: boolean = false): Promise<MetalPricesData | null> {
        // Check cache first unless force refresh
        if (!forceRefresh) {
            const cached = this.getCachedData();
            if (cached) {
                console.log('Returning cached metal prices');
                return cached;
            }
        }

        // Check if we can make an API call
        if (!this.canMakeApiCall()) {
            console.warn('API call limit reached');
            const cached = this.getCachedData();
            if (cached) return cached;
            return this.getFallbackData();
        }

        // Fetch from API
        try {
            return await this.fetchFromApi(forceRefresh);
        } catch (error) {
            console.error('Failed to fetch from API:', error);
            const cached = this.getCachedData();
            if (cached) return cached;
            return this.getFallbackData();
        }
    }

    /**
     * Get fallback data when API and cache are unavailable
     */
    private getFallbackData(): MetalPricesData {
        // Return realistic historical data as fallback
        const metals: MetalPrice[] = [
            {
                symbol: 'COPPER',
                name: 'Copper',
                price: 13210.00, // ~0.41 per oz * 32150.7
                priceAED: 48520.00,
                change: 0,
                changePercent: 0,
                unit: 'USD/MT',
                unitAED: 'AED/MT',
                lastUpdated: new Date().toISOString()
            },
            {
                symbol: 'ALUMINUM',
                name: 'Aluminum',
                price: 3045.00, // ~0.0947 per oz * 32150.7
                priceAED: 11183.00,
                change: 0,
                changePercent: 0,
                unit: 'USD/MT',
                unitAED: 'AED/MT',
                lastUpdated: new Date().toISOString()
            },
            {
                symbol: 'NICKEL',
                name: 'Nickel',
                price: 17280.00, // ~0.5374 per oz * 32150.7
                priceAED: 63456.00,
                change: 0,
                changePercent: 0,
                unit: 'USD/MT',
                unitAED: 'AED/MT',
                lastUpdated: new Date().toISOString()
            },
            {
                symbol: 'ZINC',
                name: 'Zinc',
                price: 3283.00, // ~0.1021 per oz * 32150.7
                priceAED: 12056.00,
                change: 0,
                changePercent: 0,
                unit: 'USD/MT',
                unitAED: 'AED/MT',
                lastUpdated: new Date().toISOString()
            },
            {
                symbol: 'GOLD',
                name: 'Gold',
                price: 5018.37,
                priceAED: 18430.00,
                change: 0,
                changePercent: 0,
                unit: 'USD/OZ',
                unitAED: 'AED/OZ',
                lastUpdated: new Date().toISOString()
            },
            {
                symbol: 'SILVER',
                name: 'Silver',
                price: 89.41,
                priceAED: 328.40,
                change: 0,
                changePercent: 0,
                unit: 'USD/OZ',
                unitAED: 'AED/OZ',
                lastUpdated: new Date().toISOString()
            },
            {
                symbol: 'LEAD',
                name: 'Lead',
                price: 1955.00, // ~0.0608 per oz * 32150.7
                priceAED: 7178.00,
                change: 0,
                changePercent: 0,
                unit: 'USD/MT',
                unitAED: 'AED/MT',
                lastUpdated: new Date().toISOString()
            }
        ];

        return {
            metals,
            timestamp: Date.now(),
            source: 'cache'
        };
    }

    /**
     * Get rate limit status
     */
    public getRateLimitStatus(): { used: number; limit: number; remaining: number; resetDate: string } {
        const tracker = this.getRateLimitTracker();
        return {
            used: tracker.callCount,
            limit: MONTHLY_CALL_LIMIT,
            remaining: MONTHLY_CALL_LIMIT - tracker.callCount,
            resetDate: tracker.resetDate
        };
    }
}

// Export singleton instance
export const metalPriceService = new MetalPriceService();
